import { Typography } from '@mui/material';
import FlexContainer from 'components/FlexContainer';
import { HelpLinkingButton } from 'components/HelpLinkingButton';
import { useUpdateDocumentBrandVoice } from 'features/aiWriter/AiWriterSidebar/steps/chat/useUpdateDocumentBrandVoice';
import { isAiWriterSupportedLanguageAndCountryCode } from 'features/aiWriter/aiWriterSupportedLanguageAndCountryCodes';
import { TonalitySelect } from 'features/aiWriter/tonality/TonalitySelect';
import { BrandVoiceAutocomplete } from 'features/brand-voice/BrandVoiceAutocomplete';
import { getEmbeddingModelDataSelector } from 'features/embeddingModels/store/selectors';
import FormattedMessage from 'features/i18n/FormattedMessage';
import { ApplyInformationList } from 'features/information/apply-information/ApplyInformationList';
import { useShowApplyInformationModal } from 'features/information/apply-information/ApplyInformationModal';
import { TargetAudienceAutocomplete } from 'features/target-audience/TargetAudienceAutocomplete';
import { FormValues } from 'features/workflowBlogPostBuilder/types';
import { useFormikContext } from 'formik';
import { GAEvents } from 'services/tracking/GAEvents';
import gtmIds from 'services/tracking/GTMIds';
import { withGtmInteraction } from 'services/tracking/withGtmInteraction';
import { useAppSelector } from 'store/hooks';
import styled from 'styled-components';
import useTr from 'utils/hooks/useTr';

import * as CommonStyled from './common.styles';

export const Step2 = () => {
  const getModelById = useAppSelector(getEmbeddingModelDataSelector);
  const translate = useTr();

  const { values, setFieldValue } = useFormikContext<FormValues>();

  const model = getModelById(values.language);
  const languageAndCountryCode = `${model.language}_${model.country}`;

  const tonalitySupportedInLanguage =
    isAiWriterSupportedLanguageAndCountryCode(languageAndCountryCode);

  const updateBrandVoice = useUpdateDocumentBrandVoice();

  const handleBrandVoiceChange = (brandVoiceId?: string) => {
    updateBrandVoice({ brandVoiceId });

    setFieldValue('brandVoiceId', brandVoiceId);
  };

  const showApplyInformationModal = useShowApplyInformationModal();

  const handleInformationApply = () => {
    showApplyInformationModal({
      preselectedInformation: values.information,
      onApply: informationList => {
        setFieldValue('information', informationList);
      },
    });
  };

  const handleInformationRemove = (id: number) => {
    setFieldValue(
      'information',
      values.information?.filter(information => information.id !== id)
    );
  };

  const handleTargetAudienceChange = (targetAudienceId: string | undefined) => {
    GAEvents.blogPostWorkflowTargetAudienceSelected({
      targetAudienceId,
    });
    setFieldValue('targetAudienceId', targetAudienceId);
  };

  if (!tonalitySupportedInLanguage) {
    return null;
  }

  return (
    <>
      <CommonStyled.Title>
        <FormattedMessage id="blog_post_builder.steps.2.title" />
      </CommonStyled.Title>

      <BrandVoiceContainer>
        <FlexContainer direction="row" gap="small">
          <Typography variant="subtitle1">
            <FormattedMessage id="common.brandVoice" />
          </Typography>

          <HelpLinkingButton
            link="aiWriter.inspirations.aiwriter.main.personality.help_link"
            tooltip={<FormattedMessage id="blog_post_builder.tonality_step.personality.tooltip" />}
          />
        </FlexContainer>
        <SelectorContainer>
          <BrandVoiceAutocomplete
            targetLanguage={model.language}
            targetCountry={model.country}
            initialBrandVoiceId={values.brandVoiceId}
            onBrandVoiceIdChange={handleBrandVoiceChange}
            fullWidth={true}
          />
        </SelectorContainer>
      </BrandVoiceContainer>

      <SelectorContainer gap="two">
        <Typography variant="subtitle1">
          <FormattedMessage id="common.target_audience" />
        </Typography>
        <TargetAudienceAutocomplete
          targetLanguage={model.language}
          targetCountry={model.country}
          initialTargetAudienceId={values.targetAudienceId}
          onTargetAudienceIdChange={handleTargetAudienceChange}
          fullWidth={true}
        />
      </SelectorContainer>

      <TonalitySelect
        label={translate('blog_post_builder.tonality_step.label')}
        systemTonality={values.systemTonality}
        userTonality={values.userTonality}
        maxLength={2}
        languageAndCountryCode={languageAndCountryCode}
        onChange={(newSystemTonalities, newUserTonalities) => {
          setFieldValue('systemTonality', newSystemTonalities);
          setFieldValue('userTonality', newUserTonalities);
        }}
        onInputChange={inputValue => {
          // Disable nex step if user entered a tonality but didn't confirmed it
          setFieldValue('disableNextStep', inputValue.length > 0);
        }}
        gtmAttributes={withGtmInteraction(gtmIds.blogPostBuilder.tonality)}
      />

      <ApplyInformationList
        informationList={values.information}
        onApply={handleInformationApply}
        onRemove={handleInformationRemove}
        fontVariant="subtitle1"
      />
    </>
  );
};

const BrandVoiceContainer = styled(FlexContainer).attrs({
  direction: 'column',
  justifyContent: 'space-between',
  gap: 'two',
})``;

const SelectorContainer = styled(FlexContainer)`
  width: 20rem;
`;
